import { useState, useEffect, useCallback } from 'react'
import { Component, LocaleCode } from '../types/component'
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover'

interface CartProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
  cartId?: string | null
  onCartUpdate?: (cartId: string) => void
}

interface CartItem {
  id: string
  product_id: number
  variant_id?: number
  name: string
  quantity: number
  list_price: number
  sale_price?: number
  image_url?: string
  sku?: string
}

interface CartData {
  id: string
  line_items: {
    physical_items: CartItem[]
    digital_items: CartItem[]
  }
  base_amount: number
  cart_amount: number
  currency: {
    code: string
  }
}

export function Cart({ component, locale, storeId, apiUrl, style, cartId, onCartUpdate }: CartProps) {
  const [cartData, setCartData] = useState<CartData | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const config = component.config as any

  const displayStyle = (config.displayStyle as string) || 'popover'
  const buttonText = (config.buttonText as string) || ''
  const showItemCount = (config.showItemCount as boolean) !== false

  const loadCart = useCallback(async () => {
    if (!cartId || !storeId) return

    try {
      setIsLoading(true)
      const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}/${cartId}`)
      if (!response.ok) {
        if (response.status === 404) {
          setCartData(null)
          if (onCartUpdate) {
            onCartUpdate('')
          }
        }
        throw new Error('Failed to load cart')
      }
      const data = await response.json()
      setCartData(data.data)
    } catch (error) {
      console.error('Failed to load cart:', error)
      setCartData(null)
    } finally {
      setIsLoading(false)
    }
  }, [cartId, storeId, apiUrl, onCartUpdate])

  useEffect(() => {
    if (cartId && storeId) {
      loadCart()
    }
  }, [cartId, storeId, loadCart])

  useEffect(() => {
    if (isOpen && cartId && storeId) {
      loadCart()
    }
  }, [isOpen, cartId, storeId, loadCart])

  const getTotalItems = () => {
    if (!cartData) return 0
    const physicalCount = cartData.line_items.physical_items.reduce((sum, item) => sum + item.quantity, 0)
    const digitalCount = cartData.line_items.digital_items.reduce((sum, item) => sum + item.quantity, 0)
    return physicalCount + digitalCount
  }

  const allItems = cartData
    ? [...cartData.line_items.physical_items, ...cartData.line_items.digital_items]
    : []

  const formatPrice = (price: number) => {
    const currency = cartData?.currency.code || 'USD'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(price)
  }

  const handleUpdateQuantity = async (itemId: string, newQuantity: number, productId: number) => {
    if (!cartId || !storeId || newQuantity < 1) return

    try {
      const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}/${cartId}/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQuantity, product_id: productId }),
      })
      if (!response.ok) throw new Error('Failed to update quantity')
      await loadCart()
    } catch (error) {
      console.error('Failed to update quantity:', error)
    }
  }

  const handleRemoveItem = async (itemId: string) => {
    if (!cartId || !storeId) return

    try {
      const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}/${cartId}/items/${itemId}`, {
        method: 'DELETE',
      })
      if (!response.ok) throw new Error('Failed to remove item')
      await loadCart()
    } catch (error) {
      console.error('Failed to remove item:', error)
    }
  }

  const renderCartButton = () => {
    const isIconStyle = displayStyle === 'icon'

    return (
      <button className={
        isIconStyle
          ? "relative p-2 hover:bg-accent rounded transition-colors"
          : "relative flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90 transition-colors"
      }>
        {/* Cart Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>

        {buttonText && !isIconStyle && <span>{buttonText}</span>}

        {/* Item Count Badge */}
        {showItemCount && getTotalItems() > 0 && (
          <span className={
            isIconStyle
              ? "absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium"
              : "absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center"
          }>
            {getTotalItems()}
          </span>
        )}
      </button>
    )
  }

  const renderCartContent = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold text-lg">Shopping Cart</h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-muted-foreground hover:text-foreground transition-colors text-2xl leading-none"
        >
          ×
        </button>
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading cart...</div>
        ) : allItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>Your cart is empty</p>
          </div>
        ) : (
          <div className="space-y-4">
            {allItems.map((item) => (
              <div key={item.id} className="flex gap-3 pb-4 border-b last:border-b-0">
                {/* Product Image */}
                <div className="w-20 h-20 bg-muted rounded overflow-hidden flex-shrink-0">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                      No Image
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-medium text-sm line-clamp-2 flex-1">{item.name}</h4>
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors ml-2"
                      title="Remove item"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                  {item.sku && <p className="text-xs text-muted-foreground mb-2">SKU: {item.sku}</p>}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Qty:</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1, item.product_id)}
                          className="w-6 h-6 flex items-center justify-center border rounded hover:bg-accent transition-colors"
                          disabled={item.quantity <= 1}
                        >
                          −
                        </button>
                        <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity + 1, item.product_id)}
                          className="w-6 h-6 flex items-center justify-center border rounded hover:bg-accent transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      {item.sale_price && item.sale_price < item.list_price ? (
                        <>
                          <p className="text-sm font-bold">{formatPrice(item.sale_price * item.quantity)}</p>
                          <p className="text-xs text-muted-foreground line-through">
                            {formatPrice(item.list_price * item.quantity)}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm font-bold">{formatPrice(item.list_price * item.quantity)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {cartData && allItems.length > 0 && (
        <div className="border-t p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium">{formatPrice(cartData.base_amount)}</span>
          </div>
          <div className="flex items-center justify-between text-lg font-bold">
            <span>Total</span>
            <span>{formatPrice(cartData.cart_amount)}</span>
          </div>
          <button className="w-full px-4 py-3 bg-primary text-primary-foreground rounded font-medium hover:opacity-90 transition-colors">
            Checkout
          </button>
        </div>
      )}
    </div>
  )

  // Render popover style
  if (displayStyle === 'popover') {
    return (
      <div style={style}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            {renderCartButton()}
          </PopoverTrigger>
          <PopoverContent align="end" className="w-96 max-h-[600px] p-0">
            {renderCartContent()}
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  // Render sidebar style
  return (
    <div style={style} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90 transition-colors"
      >
        {/* Cart Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>

        {buttonText && <span>{buttonText}</span>}

        {/* Item Count Badge */}
        {showItemCount && getTotalItems() > 0 && (
          <span className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {getTotalItems()}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-96 bg-background shadow-2xl z-50 animate-slide-in-right">
            {renderCartContent()}
          </div>
        </>
      )}
    </div>
  )
}
